import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Copyright } from 'lucide-react';
import { getLegalPage } from '../utils/api';

const CopyrightPolicy = () => {
  const [cmsContent, setCmsContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadContent = async () => {
      try {
        const res = await getLegalPage('copyright_policy');
        const data = res.data || {};
        // Only use CMS content if it has actual content
        if (data.content && data.content.trim()) {
          setCmsContent(data);
        }
      } catch (error) {
        console.error('Failed to load Copyright Policy:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadContent();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B1818]"></div>
      </div>
    );
  }

  // If CMS content exists, render it
  if (cmsContent && cmsContent.content) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <div className="py-12 bg-[#F9F9F7]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
              {cmsContent.title || 'Copyright & IP Policy'}
            </h1>
            {cmsContent.last_updated_date && (
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Last updated: {cmsContent.last_updated_date}
              </p>
            )}
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link to="/" className="inline-flex items-center text-[#8B1818] hover:text-[#701313] mb-8">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
            <div className="prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: cmsContent.content }} />
          </div>
        </div>
      </div>
    );
  }

  // Default static content (original page)
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="py-12 bg-[#F9F9F7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
            Copyright & IP Policy
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Last updated: December 8, 2025
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center text-[#8B1818] hover:text-[#701313] mb-8">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          <div className="prose prose-slate max-w-none space-y-6">
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Ownership of Content</h2>
              <p className="text-slate-700">
                All content on the <span className="text-[#8B1818] font-semibold">Lex</span>DoRight website, including but not limited to text, graphics, logos, images, audio clips, digital downloads, data compilations, and software, is the property of <span className="text-[#8B1818] font-semibold">Lex</span>DoRight or its content suppliers and is protected by Philippine and international copyright laws.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Trademarks</h2>
              <p className="text-slate-700">
                The <span className="text-[#8B1818] font-semibold">Lex</span>DoRight name, logo, and all related names, logos, product and service names, designs, and slogans are trademarks of <span className="text-[#8B1818] font-semibold">Lex</span>DoRight. You may not use such marks without our prior written permission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Permitted Use</h2>
              <p className="text-slate-700">You may:</p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>View and access our content for personal, non-commercial use</li>
                <li>Print or download pages for personal reference</li>
                <li>Share links to our content with proper attribution</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Prohibited Use</h2>
              <p className="text-slate-700">You may not:</p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>Reproduce, distribute, or republish our content without permission</li>
                <li>Modify or create derivative works from our content</li>
                <li>Use our content for commercial purposes without authorization</li>
                <li>Remove any copyright or proprietary notices</li>
                <li>Use our trademarks or branding without written consent</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">5. User-Generated Content</h2>
              <p className="text-slate-700">
                By posting content on our platform (such as forum posts or comments), you grant <span className="text-[#8B1818] font-semibold">Lex</span>DoRight a non-exclusive, royalty-free, perpetual license to use, reproduce, modify, and display such content in connection with our services.
              </p>
              <p className="text-slate-700 mt-4">
                You represent that you own or have the necessary rights to the content you post and that it does not infringe on any third party's intellectual property rights.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Copyright Infringement Claims</h2>
              <p className="text-slate-700">
                If you believe that your copyrighted work has been used on our website in a way that constitutes copyright infringement, please provide us with the following information:
              </p>
              <ul className="list-disc pl-6 text-slate-700 space-y-2">
                <li>A description of the copyrighted work you claim has been infringed</li>
                <li>The location on our website where the alleged infringing material is found</li>
                <li>Your contact information</li>
                <li>A statement that you have a good faith belief that the use is not authorized</li>
                <li>A statement, under penalty of perjury, that the information is accurate</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Third-Party Content</h2>
              <p className="text-slate-700">
                Our website may contain links to third-party websites or content. We do not claim ownership of and are not responsible for any third-party content. Such content is subject to the copyright and intellectual property policies of the respective owners.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Enforcement</h2>
              <p className="text-slate-700">
                <span className="text-[#8B1818] font-semibold">Lex</span>DoRight reserves the right to take appropriate legal action against any unauthorized use of our intellectual property, including seeking injunctive relief and damages.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Contact Us</h2>
              <p className="text-slate-700">
                For questions about this Copyright & IP Policy or to report potential infringement, please contact us at <a href="mailto:admin@lexdoright.com" className="text-[#8B1818] hover:underline">admin@lexdoright.com</a>.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CopyrightPolicy;
